/**
 * Community section sort dropdown: Choices.js (loaded from layout).
 * Initializes on .comm-page__sort; options from native select. DPR style via comm.scss.
 */

declare global {
  interface Window {
    Choices?: new (element: HTMLSelectElement | string, options?: ChoicesOptions) => ChoicesInstance;
  }
}

interface ChoicesOptions {
  searchEnabled?: boolean;
  shouldSort?: boolean;
  itemSelectText?: string;
  callbackOnCreateTemplates?: (template: string) => string;
}

interface ChoicesInstance {
  destroy: () => void;
}

export class CommSortSelect {
  private readonly selector: string;
  private instance: ChoicesInstance | null = null;

  constructor(selector: string = '.comm-page__sort') {
    this.selector = selector;
  }

  /**
   * Bind Choices to the first element matching this.selector.
   */
  init(): ChoicesInstance | null {
    const Choices = typeof window !== 'undefined' ? window.Choices : undefined;
    if (!Choices) return null;

    const el = document.querySelector<HTMLSelectElement>(this.selector);
    if (!el) return null;

    this.instance = new Choices(el, {
      searchEnabled: false,
      shouldSort: false,
      itemSelectText: '',
    });

    // Keep native change for pages that listen (e.g. form reload)
    const wrap = el.closest('.comm-page__sort-wrap, .profile-page__filters');
    const container = wrap?.querySelector('.choices') ?? el.parentElement?.querySelector('.choices');
    if (container) {
      container.addEventListener('change', () => {
        const sel = document.querySelector<HTMLSelectElement>(this.selector);
        if (sel) sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // Lenis: allow scroll inside dropdown list (overflow auto ul)
    const applyLenisPrevent = (root: Element | null): void => {
      const list = root?.querySelector('.choices__list--dropdown');
      if (list && !list.hasAttribute('data-lenis-prevent')) list.setAttribute('data-lenis-prevent', '');
    };
    applyLenisPrevent(container ?? wrap);
    requestAnimationFrame(() => applyLenisPrevent(container ?? wrap ?? el.parentElement));

    return this.instance;
  }

  getInstance(): ChoicesInstance | null {
    return this.instance;
  }

  destroy(): void {
    if (this.instance && typeof this.instance.destroy === 'function') {
      this.instance.destroy();
      this.instance = null;
    }
  }
}
